import Bolt from "@mui/icons-material/Bolt";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Close from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  type SxProps,
  Stack,
  TextField,
  Tooltip,
  Typography,
  type TextFieldProps,
  type Theme,
} from "@mui/material";
import { type FC, useCallback, useState } from "react";
import { useToken } from "../../server/auth/use-token";
import { useFlag } from "../../server/optimizely";
import { useBrandTones } from "../brand-kit/hooks/use-brand-tones";
import { AutoCompleteSelector } from "../generic/components/auto-complete/auto-complete";
import { useAccountContext } from "../generic/hooks/use-account-context";
import type { TagGroup } from "../merge-tag/components/types";
import { type EmailSubjectsResponse, MomentSubjectService } from "./moment-subject-service";

const toneSummaries = [
  "The message is in a professional yet light hearted writing style.", // professional
  "The message is in a professional yet light hearted writing style.", // casual
  "The message is in a professional, direct writing style.", // direct
];

export const EmailSubjectField: FC<{
  value: string;
  onChange: (value: string) => void;
  filterTags: (x: string) => TagGroup;
  getMessageBody: () => string;
  error?: string;
  disabled?: boolean;
  textFieldVariant?: TextFieldProps["variant"];
  textFieldSx?: SxProps<Theme>;
}> = ({
  value,
  onChange,
  filterTags,
  getMessageBody,
  error,
  disabled,
  textFieldVariant = "outlined",
  textFieldSx,
}) => {
  const getToken = useToken();
  const { data: accountContext } = useAccountContext();
  const { data: brandTones } = useBrandTones();
  const [subjectLineGenerationEnabled] = useFlag("cha-3806-subject-line-generation");
  const [subjectSuggestions, setSubjectSuggestions] = useState<
    EmailSubjectsResponse["emailSubjects"] | null
  >(null);
  const [subjectSuggestionIndex, setSubjectSuggestionIndex] = useState(0);
  const [subjectSuggestionsLoading, setSubjectSuggestionsLoading] = useState(false);
  const [subjectInputKey, setSubjectInputKey] = useState(0);
  const selectedSubjectSuggestion = subjectSuggestions?.[subjectSuggestionIndex];

  const handleSuggestSubject = useCallback(async () => {
    setSubjectSuggestionsLoading(true);
    try {
      const suggestionResponse = await new MomentSubjectService(await getToken()).suggestSubjects({
        messageBody: getMessageBody(),
        companyContext: accountContext
          ? {
              companyName: accountContext.companyName ?? undefined,
              industry: accountContext.industry ?? undefined,
              companyDescription: accountContext.companyDescription ?? undefined,
              companyValues: accountContext.companyValues ?? undefined,
              companyTeamReference: accountContext.companyTeamReference ?? undefined,
            }
          : undefined,
        toneSummaries: [
          ...toneSummaries,
          ...(brandTones?.map((brandTone) => brandTone.summary) ?? []),
        ],
      });
      setSubjectSuggestions(suggestionResponse.emailSubjects);
      setSubjectSuggestionIndex(0);
    } catch (err) {
      console.error("Error suggesting email subjects", err);
      setSubjectSuggestions(null);
    } finally {
      setSubjectSuggestionsLoading(false);
    }
  }, [accountContext, brandTones, getMessageBody, getToken]);

  const handlePreviousSubjectSuggestion = useCallback(() => {
    setSubjectSuggestionIndex((index) =>
      subjectSuggestions ? (index + subjectSuggestions.length - 1) % subjectSuggestions.length : 0,
    );
  }, [subjectSuggestions]);

  const handleNextSubjectSuggestion = useCallback(() => {
    setSubjectSuggestionIndex((index) =>
      subjectSuggestions ? (index + 1) % subjectSuggestions.length : 0,
    );
  }, [subjectSuggestions]);

  const handleDismissSubjectSuggestions = useCallback(() => {
    setSubjectSuggestions(null);
    setSubjectSuggestionIndex(0);
  }, []);

  const handleUseSubjectSuggestion = useCallback(() => {
    if (!selectedSubjectSuggestion) return;
    onChange(selectedSubjectSuggestion.subject);
    setSubjectInputKey((key) => key + 1);
  }, [onChange, selectedSubjectSuggestion]);

  return (
    <>
      <AutoCompleteSelector
        key={subjectInputKey}
        defaultValue={value}
        onChange={onChange}
        filterTags={filterTags}
        renderInput={(params) => (
          <TextField
            {...params}
            variant={textFieldVariant}
            sx={textFieldSx}
            id="emailSubject"
            label="Subject"
            placeholder="Enter your email subject"
            error={!!error}
            helperText={error ?? "Type {{ to insert a merge tag into your subject"}
            fullWidth
            disabled={disabled}
          />
        )}
      />
      {subjectLineGenerationEnabled && (
        <Box sx={{ mt: 0.5, minHeight: 32, display: "flex", alignItems: "center" }}>
          {subjectSuggestionsLoading ? (
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight={600}>
                  Suggested subject:
                </Typography>{" "}
                Loading
              </Typography>
              <CircularProgress size={14} aria-label="Loading subject suggestions" />
            </Stack>
          ) : subjectSuggestions?.length && selectedSubjectSuggestion ? (
            <Stack direction="row" alignItems="center" gap={1} sx={{ width: "100%", minWidth: 0 }}>
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ minWidth: 0, flexGrow: 1, overflow: "hidden" }}
              >
                <Tooltip title={selectedSubjectSuggestion.subject}>
                  <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                    <Typography component="span" variant="body2" fontWeight={600}>
                      Suggested subject:
                    </Typography>{" "}
                    {selectedSubjectSuggestion.subject}
                  </Typography>
                </Tooltip>
                <Button
                  type="button"
                  variant="text"
                  size="small"
                  onClick={handleUseSubjectSuggestion}
                  disabled={disabled}
                  data-analytics-id="email-subject-use-suggestion"
                  sx={{ minWidth: 0, px: 0.25, py: 0.25, flexShrink: 0 }}
                >
                  <Box component="span" sx={{ transform: "translateY(0.25px)" }}>
                    Use this suggestion
                  </Box>
                </Button>
              </Stack>
              <Stack direction="row" alignItems="center" gap={1} sx={{ ml: "auto", flexShrink: 0 }}>
                <Box sx={{ height: 20, borderLeft: 1, borderColor: "divider" }} />
                <IconButton
                  type="button"
                  size="small"
                  aria-label="Previous subject suggestion"
                  onClick={handlePreviousSubjectSuggestion}
                  disabled={disabled}
                  data-analytics-id="email-subject-previous-suggestion"
                >
                  <ChevronLeft fontSize="small" />
                </IconButton>
                <Typography variant="body2" color="text.secondary">
                  Suggestion {subjectSuggestionIndex + 1}/{subjectSuggestions.length}
                </Typography>
                <IconButton
                  type="button"
                  size="small"
                  aria-label="Next subject suggestion"
                  onClick={handleNextSubjectSuggestion}
                  disabled={disabled}
                  data-analytics-id="email-subject-next-suggestion"
                >
                  <ChevronRight fontSize="small" />
                </IconButton>
                <IconButton
                  type="button"
                  size="small"
                  aria-label="Dismiss subject suggestions"
                  onClick={handleDismissSubjectSuggestions}
                  disabled={disabled}
                  data-analytics-id="email-subject-dismiss-suggestions"
                >
                  <Close fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          ) : (
            <Button
              type="button"
              variant="text"
              size="small"
              startIcon={<Bolt fontSize="small" />}
              onClick={() => void handleSuggestSubject()}
              disabled={disabled}
              data-analytics-id="email-subject-suggest"
              sx={{ minWidth: 0, px: 0.25 }}
            >
              Suggest a subject
            </Button>
          )}
        </Box>
      )}
    </>
  );
};
